import { Cancel, CheckCircle, Circle, Delete, Edit } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import UnitSiteStatus from "components/Unit/UnitSiteStatus";
import UnitStatusIconGroup from "components/Unit/UnitStatusIconGroup";
import { MODEL_OPTIONS_TABS } from "constants/configuration";
import { format } from "date-fns";
import { capitalize, isNil } from "lodash";
import { displayDurationBetweenDates, fromNow } from "utils/datetime";
import { NavLink, Link as ReactLink } from "react-router-dom";
import SiteStatusIconGroup from "components/Site/SiteStatusIconGroup";
import SiteStatus from "components/Site/SiteStatus";

export const renderName = (params) => {
  if (!params.row?.first_name) return "";
  return <Box>{`${params.row?.first_name} ${params.row?.last_name}`}</Box>;
};

export const renderActions = (params, onClickEdit, onClickDelete) => {
  return (
    <Box className="items-center">
      <IconButton color="primary" onClick={() => onClickEdit(params.row)}>
        <Edit fontSize="small" />
      </IconButton>

      <IconButton color="primary" onClick={() => onClickDelete(params.row)}>
        <Delete fontSize="small" />
      </IconButton>
    </Box>
  );
};

export const renderDeleteActions = (params, onClickDelete) => {
  return (
    <Box className="items-center">
      <Button
        className="button-link"
        onClick={(e) => onClickDelete(e, params.row)}
      >
        <Delete fontSize="small" />
      </Button>
    </Box>
  );
};

export const renderEditActions = (params, onClickEdit) => {
  return (
    <Box className="">
      <IconButton color="primary" onClick={() => onClickEdit(params.row)}>
        <Edit fontSize="small" />
      </IconButton>
    </Box>
  );
};

export const renderAssign = (params, setAssignUser) => {
  return (
    <Button
      onClick={() => setAssignUser(params.row)}
      style={{ textDecoration: "underline" }}
    >
      Assign/Remove Sites
    </Button>
  );
};

export const renderLink = (params, link, onClick) => {
  return (
    <Button
      onClick={() => onClick(params.row)}
      style={{ textDecoration: "underline" }}
      sx={{
        p: 0,
        minWidth: 0,
        px: "4px",
      }}
    >
      {link}
    </Button>
  );
};

export const renderAnchorLink = (to, title) => (
  <ReactLink to={to} target="_blank">
    <Typography variant="subtitle2" fontWeight={500}>
      {title}
    </Typography>
  </ReactLink>
);

export const renderDate = (date, showAllInfo = true) => {
  if (!date) return "";

  return (
    <Box>{`${format(
      new Date(date),
      showAllInfo ? "dd/MM/yyyy, HH:mm:ss" : "dd/MM/yyyy, hh:mm a",
    )} ${showAllInfo ? "[Local]" : ""}`}</Box>
  );
};

export const renderStatusIcon = (value) => {
  return (
    <Box>
      {value ? (
        <CheckCircle
          sx={(theme) => ({
            color: theme.palette.success.main,
            fontSize: "20px",
          })}
        />
      ) : (
        <Cancel
          sx={(theme) => ({
            color: theme.palette.error.main,
            fontSize: "20px",
          })}
        />
      )}
    </Box>
  );
};

export const renderPercent = (value) => (
  <Box className="items-center">{value ?? 0} %</Box>
);

export const renderUnitStatus = (params) => {
  return <UnitSiteStatus status={params.row} />;
};

export const renderUnitStatusIcons = (params) => {
  const status = params.row;
  return <UnitStatusIconGroup status={status} />;
};

export const renderEnabled = (value) => (
  <Box>{value ? "Enabled" : "Disabled"}</Box>
);

export const renderModel = (value) => {
  const item = MODEL_OPTIONS_TABS.find((it) => it.value === value);
  if (item) {
    return item.label;
  }

  return value;
};

export const renderYesNo = (value) => (value ? "Yes" : "No");

export const renderFileHash = (item) => {
  const onClickDownload = (e) => {
    e.stopPropagation();
    window.open(item.download_link);
  };

  if (item.error) {
    return item.error;
  }

  if (item.download_link) {
    return (
      <Link className="ellipsize-left pointer" onClick={onClickDownload}>
        {item.file_hash}
      </Link>
    );
  }

  return <Box className="ellipsize-left">{item.file_hash}</Box>;
};

export const renderStopped = (item) => {
  let status = "";
  if (!isNil(item.closed)) {
    status = "Stopped";
  } else if (item.clear && isNil(item.closed)) {
    status = "Stopped";
  } else if (isNil(item.clear)) {
    status = "Active";
  }
  return (
    <Box
      sx={{
        color: status === "Active" ? "#FF4842" : "#212B36",
      }}
    >
      {status}
    </Box>
  );
};

export const renderDuration = (item) => {
  let start = item.set * 1000;
  let current_time = new Date();
  let end = !isNil(item.clear) ? item.clear * 1000 : current_time;
  console.log(end);
  console.log(item.clear);
  var duration = displayDurationBetweenDates(start, end);
  if (isNil(item.clear)) {
    duration += "+";
  }

  return <Box>{duration}</Box>;
};

export const renderRelativeTimeWithTooltip = (date) => {
  return renderWithDateTooltip(date, fromNow(date));
};

export const renderWithDateTooltip = (date, content) => {
  var tooltip = date ? format(new Date(date), "dd/MM/yyyy, HH:mm:ss") : "";
  return (
    <Tooltip title={tooltip}>
      <Box>{content}</Box>
    </Tooltip>
  );
};

export const renderSource = (item) => {
  return <Box>{capitalize(item.source)}</Box>;
};

export const Field = ({ label, value, onEdit = undefined, isValueNumber }) => {
  return (
    <Box className="render-field items-center" sx={{ width: "100%" }} mb={1.5}>
      <Box className="render-field-label" sx={{ width: "160px", minWidth: "160px" }}>
        <Typography variant="body2" fontWeight={500}>
          {label}
        </Typography>
      </Box>
      <Box className="render-field-value items-center" sx={{ justifyContent: isValueNumber ? 'flex-end' : 'unset' }}>
        <Typography variant="body2" fontWeight={500}>
          {value}
        </Typography>
        {onEdit && (
          <IconButton onClick={onEdit} color="primary" sx={{ ml: 0.5 }}>
            <Edit fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export const renderCost = (value) => <Box>${value.toFixed(2)}</Box>;

export const renderValue = (value) => (
  <Box>{value || value === 0 ? value : "-"}</Box>
);

export const renderExternalLink = (item) => {
  const onClickDownload = (e) => {
    e.stopPropagation();
    window.open(item.link);
  };

  if (item.error) {
    return item.error;
  }

  if (item.link) {
    return (
      <Link
        className="ellipsize-left pointer"
        onClick={onClickDownload}
        sx={{
          width: "100% !important",
          fontWeight: "500",
        }}
      >
        {item.label}
      </Link>
    );
  }

  return <Box className="ellipsize-left">{item.label}</Box>;
};

export const renderStateChange = (params) => {
  const row = params.row;
  return (
    <Box>
      {row.field} changed from {row.previous_value} to {row.new_value}
    </Box>
  );
};

export const ButtonFooter = ({ children, mt = 2, gap = 2 }) => {
  return (
    <Box className="items-center flex-end" mt={mt} gap={gap}>
      {children}
    </Box>
  );
};

export const renderTimePeriod = (params, tariffColors) => {
  const color = tariffColors[params.value];
  return (
    <Box className="items-center" sx={{ color }}>
      <Circle
        color={color}
        sx={{
          fontSize: "20px",
        }}
      />
      <Box ml={0.5}>{params.value}</Box>
    </Box>
  );
};

export const renderSiteStatus = (status) => {
  return <SiteStatus status={status} />;
};

export const renderSiteStatusIcons = (status) => {
  return <SiteStatusIconGroup status={status} />;
};

export const renderSiteUnits = (status) => {
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
      onClick={(e) => e.stopPropagation()}
    >
      {status.details.units.map((unit, i) => (
        <NavLink key={i} to={`/units/details/${unit.id}`}>
          <Tooltip title="Serial Number">
            <Typography variant="body2" fontWeight={600} lineHeight={1}>
              {unit.serial}
              {i < status.details.units.length - 1 && ","}
            </Typography>
          </Tooltip>
        </NavLink>
      ))}
    </Box>
  );
};

export const renderSiteDNSP = (status) => {
  return <Tooltip title="DNSP"><Typography variant="body2">{status?.details?.dnsp_name}</Typography></Tooltip>;
};

export const renderSiteAddress = (status) => {
  const address = status?.details;

  const parts = [
    address?.street,
    address?.city,
    address?.state,
    address?.postcode,
  ].filter(Boolean);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        minWidth: 0,
      }}
    >
      <Tooltip title="Site Address">
        <Typography variant="body2">{parts.join(", ")}</Typography>
      </Tooltip>
    </Box>
  );
};
